'use client';

import { LandingLayout } from '@/features/landing/LandingLayout';
import { useState } from 'react';
import {
  Shield,
  TrendingDown,
  Users,
  Sparkles,
  GraduationCap,
  Calendar,
  BarChart3,
  Puzzle,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  Clock,
  Target,
  Brain,
  FileText,
  AlertTriangle,
  TrendingUp,
  MessageSquare,
  Award,
  Settings,
  Activity,
} from 'lucide-react';
import Link from 'next/link';

const features = [
  {
    id: 'quality',
    icon: Shield,
    color: 'emerald',
    label: 'Quality Management',
    tagline: 'Evaluate every interaction. Elevate every agent.',
    description:
      'Build customisable evaluation templates, score agent interactions across any channel, and track quality trends with precision. From manual reviews to AI-assisted auto-scoring — Swishr covers 100% of your volume.',
    highlights: [
      {
        icon: FileText,
        title: 'Custom Evaluation Templates',
        body: 'Build scorecards with weighted sections, custom parameters, and channel-specific criteria. Support for voice, chat, email, and social.',
      },
      {
        icon: Brain,
        title: 'AI-Assisted Scoring',
        body: 'Let AI pre-score every interaction automatically. Reviewers verify and approve — cutting QA effort by up to 70% while maintaining accuracy.',
      },
      {
        icon: Target,
        title: 'Calibration Sessions',
        body: 'Run blind calibration sessions to align evaluators. Track calibration variance and ensure scoring consistency across your QA team.',
      },
      {
        icon: BarChart3,
        title: 'Performance Analytics',
        body: 'Track quality scores by agent, team, template, and time period. Drill down into parameter-level trends to identify exactly where to improve.',
      },
      {
        icon: CheckCircle,
        title: 'Audit & Compliance',
        body: 'Maintain a full audit trail of every evaluation. Set compliance parameters with zero-tolerance flags and automated escalation rules.',
      },
      {
        icon: Star,
        title: 'Scorecards & Leaderboards',
        body: 'Generate agent scorecards automatically. Leaderboards drive healthy competition and recognition across teams.',
      },
    ],
    stats: [
      { value: '100%', label: 'Interaction Coverage', sub: 'with AI scoring enabled' },
      { value: '70%', label: 'QA Time Saved', sub: 'vs. manual reviews' },
      { value: '50+', label: 'Evaluation Parameters', sub: 'customisable per template' },
    ],
  },
  {
    id: 'dsat',
    icon: TrendingDown,
    color: 'rose',
    label: 'DSAT Analysis',
    tagline: 'Turn customer dissatisfaction into your greatest asset.',
    description:
      'Stop waiting for survey responses. Swishr\'s AI analyses every interaction in real time to detect dissatisfaction signals before they become churn. Understand root causes, assign action plans, and measure impact.',
    highlights: [
      {
        icon: AlertTriangle,
        title: 'Real-Time DSAT Detection',
        body: 'AI analyses tone, escalation patterns, resolution rates, and language signals to flag dissatisfied customers — with no survey needed.',
      },
      {
        icon: Target,
        title: 'Root Cause Analysis',
        body: 'Automatically cluster DSAT causes into themes (process failures, agent issues, system outages). Ranked by frequency and business impact.',
      },
      {
        icon: Activity,
        title: 'DSAT Trend Monitoring',
        body: 'Track DSAT rates by team, channel, time of day, and issue type. Spot emerging trends before they impact customer retention.',
      },
      {
        icon: FileText,
        title: 'Action Plans',
        body: 'Create targeted action plans directly from DSAT records. Assign tasks to team leaders, set due dates, and track completion rates.',
      },
      {
        icon: BarChart3,
        title: 'Impact Measurement',
        body: 'Measure whether your interventions are working. Compare DSAT rates before and after action plans to validate improvement.',
      },
      {
        icon: MessageSquare,
        title: 'Churn Risk Scoring',
        body: 'Every customer interaction receives a churn risk score. Prioritise follow-ups and retention efforts on the highest-risk accounts.',
      },
    ],
    stats: [
      { value: '3×', label: 'Faster Detection', sub: 'vs. post-call survey methods' },
      { value: '40%', label: 'Churn Risk Reduction', sub: 'with proactive action plans' },
      { value: '100%', label: 'Interaction Analysis', sub: 'no sampling required' },
    ],
  },
  {
    id: 'wfm',
    icon: Users,
    color: 'blue',
    label: 'Workforce Management',
    tagline: 'Right people, right time. Every time.',
    description:
      'Swishr\'s WFM engine uses historical data and AI forecasting to optimise staffing levels, reduce costs, and hit your SLA targets — without the spreadsheet headaches.',
    highlights: [
      {
        icon: TrendingUp,
        title: 'AI Volume Forecasting',
        body: 'Upload historical interaction data and Swishr generates accurate volume forecasts by interval, channel, and skill group — accounting for seasonality and trends.',
      },
      {
        icon: Zap,
        title: 'Instant Schedule Generation',
        body: 'From forecast to fully optimised schedule in seconds. Configure shift patterns, break rules, skill requirements, and headcount targets.',
      },
      {
        icon: Activity,
        title: 'Real-Time Adherence',
        body: 'Live adherence dashboard shows agent schedule vs. actual activity. Set tolerance thresholds and receive alerts for significant deviations.',
      },
      {
        icon: Calendar,
        title: 'Availability Management',
        body: 'Agents can submit availability preferences and time-off requests. Managers approve or reject within the platform, with calendar sync support.',
      },
      {
        icon: Settings,
        title: 'Shift Swap Management',
        body: 'Enable peer-to-peer shift swaps with manager approval workflows. Track all swap requests and approvals in one place.',
      },
      {
        icon: BarChart3,
        title: 'Cost & SLA Modelling',
        body: 'Model different staffing scenarios to balance cost and service level. See the impact of headcount changes on SLA before you commit.',
      },
    ],
    stats: [
      { value: '92%', label: 'Forecast Accuracy', sub: 'at 30-minute intervals' },
      { value: '20%', label: 'Cost Reduction', sub: 'vs. manual scheduling' },
      { value: '15 sec', label: 'Schedule Generation', sub: 'from forecast to published' },
    ],
  },
  {
    id: 'ai',
    icon: Sparkles,
    color: 'violet',
    label: 'AI Insights',
    tagline: 'Decisions backed by intelligence, not instinct.',
    description:
      'Swishr\'s AI engine connects the dots across quality, DSAT, performance, and workforce data — surfacing insights and recommendations that no human analyst could produce at this speed or scale.',
    highlights: [
      {
        icon: Brain,
        title: 'Predictive Performance Signals',
        body: 'AI monitors agent performance trajectories and flags early warning signs of decline — giving managers time to intervene before quality drops.',
      },
      {
        icon: AlertTriangle,
        title: 'Churn Risk Identification',
        body: 'Cross-reference DSAT signals, interaction quality, and customer history to generate churn risk scores with confidence ratings.',
      },
      {
        icon: Target,
        title: 'Next Best Action Engine',
        body: 'For every at-risk agent or customer, Swishr recommends the most effective next action — coaching, escalation, call-back, or process change.',
      },
      {
        icon: TrendingUp,
        title: 'Trend & Anomaly Detection',
        body: 'Automatically detect unusual patterns in quality scores, DSAT rates, or adherence metrics. Get alerted before anomalies become systemic.',
      },
      {
        icon: Sparkles,
        title: 'Natural Language Insights',
        body: 'Ask questions about your contact center data in plain language and receive AI-generated answers with supporting data and charts.',
      },
      {
        icon: BarChart3,
        title: 'Cross-Module Intelligence',
        body: 'Unlike siloed tools, Swishr AI connects quality, WFM, DSAT, and coaching data to surface holistic insights no single module could generate alone.',
      },
    ],
    stats: [
      { value: '91%', label: 'Prediction Accuracy', sub: 'on DSAT probability scores' },
      { value: '<2 min', label: 'Insight Generation', sub: 'from data to recommendation' },
      { value: '5×', label: 'More Insights', sub: 'vs. manual analysis' },
    ],
  },
  {
    id: 'coaching',
    icon: GraduationCap,
    color: 'amber',
    label: 'Coaching & Learning',
    tagline: 'Build a culture of continuous improvement.',
    description:
      'From AI-generated coaching recommendations to structured quiz programmes, Swishr turns performance data into agent development. Track learning, measure improvement, and recognise top performers.',
    highlights: [
      {
        icon: Target,
        title: 'Data-Driven Coaching Plans',
        body: 'Coaching plans are generated from actual evaluation data — not guesswork. Focus areas, target metrics, and recommended actions are AI-suggested based on agent performance.',
      },
      {
        icon: FileText,
        title: 'Quiz Builder',
        body: 'Create knowledge assessments with multiple choice, true/false, and open-ended questions. Build question banks by topic and reuse across quizzes.',
      },
      {
        icon: Award,
        title: 'Leaderboards & Recognition',
        body: 'Gamify learning with team and organisation-wide leaderboards. Top performers are automatically recognised, driving engagement and healthy competition.',
      },
      {
        icon: BarChart3,
        title: 'Learning Analytics',
        body: 'Track quiz completion rates, average scores, knowledge gaps, and time-to-complete by agent, team, and topic area.',
      },
      {
        icon: CheckCircle,
        title: 'Coaching Progress Tracking',
        body: 'Track coaching plan completion and measure the correlation between coaching activity and quality score improvement.',
      },
      {
        icon: Calendar,
        title: 'Scheduled Assignments',
        body: 'Assign quizzes and coaching tasks with deadlines. Automated reminders keep agents on track without manager follow-up.',
      },
    ],
    stats: [
      { value: '35%', label: 'Quality Improvement', sub: 'for coached agents in 30 days' },
      { value: '85%', label: 'Quiz Completion Rate', sub: 'with automated reminders' },
      { value: '2×', label: 'Faster Onboarding', sub: 'vs. traditional methods' },
    ],
  },
  {
    id: 'scheduling',
    icon: Calendar,
    color: 'sky',
    label: 'Scheduling',
    tagline: 'Schedules your team will actually follow.',
    description:
      'Beyond WFM forecasting — Swishr\'s scheduling module handles the daily operational reality: shift assignments, swap requests, adherence reports, and real-time visibility for supervisors.',
    highlights: [
      {
        icon: Calendar,
        title: 'Visual Schedule Builder',
        body: 'Intuitive drag-and-drop schedule interface. View schedules by day, week, or month. Filter by team, skill group, or individual agent.',
      },
      {
        icon: Activity,
        title: 'Live Adherence Dashboard',
        body: 'See every agent\'s current status vs. scheduled activity in real time. Colour-coded indicators make it instant to spot who is out of adherence.',
      },
      {
        icon: Settings,
        title: 'Shift Swap Workflow',
        body: 'Agents request shift swaps directly in the platform. Managers review and approve with full visibility of coverage impact before confirming.',
      },
      {
        icon: FileText,
        title: 'Adherence Reports',
        body: 'Weekly and monthly adherence reports by agent and team. Track patterns, identify habitual non-adherence, and support performance conversations.',
      },
      {
        icon: Clock,
        title: 'Schedule Publishing',
        body: 'Publish schedules to agents with one click. Agents receive notifications and can view their schedules from any device.',
      },
      {
        icon: Users,
        title: 'Multi-Team Support',
        body: 'Manage schedules across multiple teams, departments, and locations from a single platform. Full role-based access control.',
      },
    ],
    stats: [
      { value: '98%', label: 'Schedule Adherence', sub: 'achieved by top Swishr teams' },
      { value: '80%', label: 'Less Admin Time', sub: 'vs. spreadsheet scheduling' },
      { value: '0', label: 'Scheduling Conflicts', sub: 'with automated overlap detection' },
    ],
  },
  {
    id: 'integrations',
    icon: Puzzle,
    color: 'orange',
    label: 'Integrations',
    tagline: 'Works with the tools you already use.',
    description:
      'Swishr connects to your existing tech stack out of the box. From CRM and telephony to ticketing and BI tools — data flows in automatically so your team can focus on insights, not imports.',
    highlights: [
      {
        icon: Zap,
        title: 'Native Integrations',
        body: 'Pre-built connectors for Zendesk, Salesforce, Freshdesk, Genesys, Amazon Connect, Avaya, and more. Configure in minutes, not weeks.',
      },
      {
        icon: Settings,
        title: 'REST API',
        body: 'Full REST API access for custom integrations. Push interaction data, pull reports, trigger coaching plans, or sync quality scores to any system.',
      },
      {
        icon: Activity,
        title: 'Webhook Support',
        body: 'Set up real-time webhooks to push Swishr events (new DSAT, schedule published, coaching plan assigned) to external systems instantly.',
      },
      {
        icon: FileText,
        title: 'Data Import / Export',
        body: 'Bulk import historical interaction data via CSV. Export any report or dataset in CSV, Excel, or JSON format for use in your BI tools.',
      },
      {
        icon: Shield,
        title: 'SSO & Identity',
        body: 'Enterprise SSO support via SAML 2.0 and OAuth 2.0. Integrate with Azure AD, Google Workspace, Okta, and other identity providers.',
      },
      {
        icon: BarChart3,
        title: 'BI Tool Connectors',
        body: 'Connect Swishr data to Power BI, Tableau, Looker, or Google Data Studio. Build custom dashboards using your preferred visualisation tool.',
      },
    ],
    stats: [
      { value: '20+', label: 'Native Integrations', sub: 'and growing every quarter' },
      { value: '<5 min', label: 'Setup Time', sub: 'for most native integrations' },
      { value: '99.9%', label: 'API Uptime', sub: 'with full status monitoring' },
    ],
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; badge: string; stat: string }> = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', stat: 'text-emerald-600' },
  rose:    { bg: 'bg-rose-500/10',    text: 'text-rose-600',    border: 'border-rose-500/20',    badge: 'bg-rose-50 text-rose-700 border-rose-200',       stat: 'text-rose-600' },
  blue:    { bg: 'bg-blue-500/10',    text: 'text-blue-600',    border: 'border-blue-500/20',    badge: 'bg-blue-50 text-blue-700 border-blue-200',       stat: 'text-blue-600' },
  violet:  { bg: 'bg-violet-500/10',  text: 'text-violet-600',  border: 'border-violet-500/20',  badge: 'bg-violet-50 text-violet-700 border-violet-200', stat: 'text-violet-600' },
  amber:   { bg: 'bg-amber-500/10',   text: 'text-amber-600',   border: 'border-amber-500/20',   badge: 'bg-amber-50 text-amber-700 border-amber-200',   stat: 'text-amber-600' },
  sky:     { bg: 'bg-sky-500/10',     text: 'text-sky-600',     border: 'border-sky-500/20',     badge: 'bg-sky-50 text-sky-700 border-sky-200',         stat: 'text-sky-600' },
  orange:  { bg: 'bg-orange-500/10',  text: 'text-orange-600',  border: 'border-orange-500/20',  badge: 'bg-orange-50 text-orange-700 border-orange-200', stat: 'text-orange-600' },
};

export default function FeaturesPage() {
  const [active, setActive] = useState('quality');
  const current = features.find((f) => f.id === active)!;
  const c = colorMap[current.color];
  const Icon = current.icon;

  return (
    <LandingLayout>
      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="border-b border-border bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16 max-w-6xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Full Platform Overview
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 tracking-tight">
              Everything your contact center needs.<br />
              <span className="text-primary">In one platform.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Swishr unifies quality management, DSAT analysis, workforce management, AI coaching, scheduling, and insights — purpose-built for contact centers in the MENA region.
            </p>
          </div>

          {/* Quick stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
            {[
              { value: '7', label: 'Core Modules' },
              { value: '20+', label: 'Integrations' },
              { value: '100%', label: 'Interaction Coverage' },
              { value: '15 sec', label: 'Schedule Generation' },
            ].map((s) => (
              <div key={s.label} className="border border-border rounded-xl p-4 text-center bg-background">
                <div className="text-2xl font-bold text-primary mb-1">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-24 max-w-6xl">

        {/* ── Tab Navigation ───────────────────────────────── */}
        <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {features.map((f) => {
              const FIcon = f.icon;
              const isActive = active === f.id;
              const fc = colorMap[f.color];
              return (
                <button
                  key={f.id}
                  onClick={() => setActive(f.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? `${fc.bg} ${fc.text} ${fc.border} border`
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <FIcon className="w-4 h-4" />
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Active Feature Detail ─────────────────────────── */}
        <div key={active}>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10">
            <div className={`w-16 h-16 rounded-2xl ${c.bg} flex items-center justify-center shrink-0`}>
              <Icon className={`w-8 h-8 ${c.text}`} />
            </div>
            <div>
              <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border mb-3 ${c.badge}`}>
                {current.label}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3 leading-tight">
                {current.tagline}
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-3xl text-base">
                {current.description}
              </p>
            </div>
          </div>

          {/* Stats strip */}
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 p-6 rounded-2xl border ${c.border} ${c.bg}`}>
            {current.stats.map((stat) => (
              <div key={stat.label} className="text-center sm:text-left">
                <div className={`text-4xl font-bold mb-1 ${c.text}`}>{stat.value}</div>
                <div className="text-sm font-semibold text-foreground">{stat.label}</div>
                <div className="text-xs text-muted-foreground">{stat.sub}</div>
              </div>
            ))}
          </div>

          {/* Highlights grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {current.highlights.map((h) => {
              const HIcon = h.icon;
              return (
                <div
                  key={h.title}
                  className="border border-border rounded-xl p-6 bg-background hover:border-primary/30 hover:shadow-sm transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center mb-4`}>
                    <HIcon className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {h.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{h.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Feature Overview Grid ─────────────────────────── */}
        <div className="mt-24">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground mb-2">All Platform Modules</h2>
            <p className="text-muted-foreground">Click any module to explore in detail</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {features.map((f) => {
              const FIcon = f.icon;
              const fc = colorMap[f.color];
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => { setActive(f.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`border rounded-xl p-5 text-left transition-all hover:shadow-sm ${
                    isActive ? `${fc.border} ${fc.bg}` : 'border-border hover:border-primary/20 bg-background'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg ${fc.bg} flex items-center justify-center mb-3`}>
                    <FIcon className={`w-4 h-4 ${fc.text}`} />
                  </div>
                  <div className="text-sm font-semibold text-foreground mb-1">{f.label}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{f.description.slice(0, 70)}...</div>
                </button>
              );
            })}
            {/* Placeholder for more */}
            <div className="border border-dashed border-border rounded-xl p-5 flex flex-col items-center justify-center text-center">
              <Zap className="w-6 h-6 text-muted-foreground mb-2" />
              <div className="text-sm font-medium text-muted-foreground">More coming soon</div>
              <div className="text-xs text-muted-foreground/60 mt-1">We ship new features every month</div>
            </div>
          </div>
        </div>

        {/* ── CTA ──────────────────────────────────────────── */}
        <div className="mt-20 rounded-2xl bg-foreground p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
          <div className="absolute top-0 right-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-bold text-background mb-3">
              Ready to see Swishr in action?
            </h2>
            <p className="text-background/60 mb-8 max-w-md mx-auto">
              Start your free trial today. No credit card required. Full access to all modules.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/request-access"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Request Access <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/support/help"
                className="inline-flex items-center justify-center gap-2 border border-background/20 text-background px-6 py-3 rounded-lg font-medium hover:bg-background/10 transition-colors"
              >
                Explore Help Center
              </Link>
            </div>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
